import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from '../../api/axios';

const ContactPeoplesList = () => {
    const navigate = useNavigate();

    // State management
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterInterest, setFilterInterest] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
    const [selectedRows, setSelectedRows] = useState(new Set());
    const [bulkAction, setBulkAction] = useState('');
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [selectedContact, setSelectedContact] = useState(null);
    const [deleteBtnLoading, setDeleteBtnLoading] = useState(false);
    const [expandedMessages, setExpandedMessages] = useState(new Set());
    const [activeTab, setActiveTab] = useState('website-contacts'); // 'delhi-event' or 'website-contacts'

    // Fetch contacted peoples list
    const getContactedPeoplesList = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            // const response = await axios.get('/contact-form');
            const response = await axios.get('expo-event-data');
            console.log("Response of the Expo Event :", response);
            
            if (response?.data?.data?.expoEventDataEntries) {
                setData(response?.data?.data?.expoEventDataEntries);
            } else {
                throw new Error('Invalid data format received');
            }
        } catch (error) {
            console.log("Error :", error);
            setError(error.response?.data?.message || error.message || 'Failed to fetch contacted peoples');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        getContactedPeoplesList();
    }, [getContactedPeoplesList]);

    // Memoized filtered data
    const filteredData = useMemo(() => {
        let result = data;

        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            result = result.filter(item =>
                item.name?.toLowerCase().includes(term) ||
                item.email?.toLowerCase().includes(term) ||
                item.mobile?.includes(searchTerm) ||
                item.city?.toLowerCase().includes(term)
            );
        }

        if (filterInterest !== 'all') {
            result = result.filter(item => item.interested_in === filterInterest);
        }

        return result;
    }, [data, searchTerm, filterInterest]);

    // Memoized sorted data
    const sortedData = useMemo(() => {
        if (!sortConfig.key) return filteredData;

        return [...filteredData].sort((a, b) => {
            let aValue = a[sortConfig.key];
            let bValue = b[sortConfig.key];

            if (sortConfig.key === 'created_at' || sortConfig.key === 'updated_at') {
                aValue = new Date(aValue).getTime();
                bValue = new Date(bValue).getTime();
            }

            if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
    }, [filteredData, sortConfig]);

    // Pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = sortedData.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(sortedData.length / itemsPerPage);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, filterInterest]);

    const handleSort = (key) => {
        setSortConfig(current => ({
            key,
            direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const handleSelectRow = (id) => {
        setSelectedRows(prev => {
            const newSet = new Set(prev);
            if (newSet.has(id)) newSet.delete(id);
            else newSet.add(id);
            return newSet;
        });
    };

    const handleSelectAll = () => {
        if (selectedRows.size === currentItems.length) {
            setSelectedRows(new Set());
        } else {
            setSelectedRows(new Set(currentItems.map(item => item.id)));
        }
    };

    const toggleMessageExpand = (id) => {
        setExpandedMessages(prev => {
            const newSet = new Set(prev);
            if (newSet.has(id)) newSet.delete(id);
            else newSet.add(id);
            return newSet;
        });
    };

    const handleOpenDeleteModal = (contact) => {
        setSelectedContact(contact);
        setOpenDeleteModal(true);
    };

    const handleDelete = async () => {
        if (!selectedContact) return;

        setDeleteBtnLoading(true);
        try {
            const response = await axios.delete(`expo-event-data/${selectedContact.id}`);
            if (response.status === 200) {
                toast.success('Contact deleted successfully!');
                await getContactedPeoplesList();
                setOpenDeleteModal(false);
                setSelectedContact(null);
            }
        } catch (error) {
            toast.error('Failed to delete contact. Please try again.');
        } finally {
            setDeleteBtnLoading(false);
        }
    };

    const handleBulkAction = async () => {
        if (!bulkAction || selectedRows.size === 0) return;

        try {
            setLoading(true);

            switch (bulkAction) {
                case 'delete':
                    if (window.confirm(`Delete ${selectedRows.size} contact(s)?`)) {
                        await Promise.all(
                            Array.from(selectedRows).map(id => axios.delete(`/contact-form/${id}`))
                        );
                        toast.success(`${selectedRows.size} contact(s) deleted successfully!`);
                    }
                    break;
                case 'export':
                    exportSelectedData();
                    return;
                default:
                    return;
            }

            await getContactedPeoplesList();
            setSelectedRows(new Set());
            setBulkAction('');
        } catch (err) {
            toast.error('Bulk action failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const exportSelectedData = () => {
        const dataToExport = selectedRows.size > 0
            ? data.filter(item => selectedRows.has(item.id))
            : sortedData;

        if (dataToExport.length === 0) {
            toast.warning('No data available to export.');
            return;
        }

        const headers = ['Name', 'Email', 'Mobile', 'Interest', 'City', 'Date Contacted'];
        const csvContent = [
            headers.join(','),
            ...dataToExport.map(item =>
                [
                    `"${item.name || 'N/A'}"`,
                    `"${item.email || 'N/A'}"`,
                    `"${item.mobile || 'N/A'}"`,
                    `"${item.interested_in || 'N/A'}"`,
                    `"${(item.city || 'N/A').replace(/"/g, '""')}"`,
                    `"${formatDate(item.created_at)}"`
                ].join(',')
            )
        ].join('\n');

        const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `contacts-${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        URL.revokeObjectURL(url);

        toast.success(`${dataToExport.length} records exported!`);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getInterestBadge = (interest) => {
        const styles = {
            'DISTRIBUTION': { bg: '#e3f2fd', color: '#0d47a1', label: 'Distribution' },
            'FRANCHISE': { bg: '#e8f5e8', color: '#1b5e20', label: 'Franchise' },
            'MEDIA': { bg: '#fff3e0', color: '#e65100', label: 'Media' }
        };
        const style = styles[interest] || { bg: '#f5f5f5', color: '#616161', label: interest };

        return {
            backgroundColor: style.bg,
            color: style.color,
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '0.75rem',
            fontWeight: '500',
            display: 'inline-block'
        };
    };

    const getSortIndicator = (key) => {
        if (sortConfig.key !== key) return '↕️';
        return sortConfig.direction === 'asc' ? '↑' : '↓';
    };

    const handleLogout = () => {
        localStorage.removeItem("authToken");
        navigate('/');
        toast.info('Logged out successfully');
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        if (tab === 'delhi-event') {
            navigate('/listing');
        } else {
            navigate('/enquiry-listing');
        }
    };

    // Loading state
    if (loading && data.length === 0) {
        return (
            <div style={styles.loadingContainer}>
                <div style={styles.loadingSpinner}></div>
                <p style={styles.loadingText}>Loading contacts...</p>
            </div>
        );
    }

    // Error state
    if (error && data.length === 0) {
        return (
            <div style={styles.errorContainer}>
                <div style={styles.errorIcon}>⚠️</div>
                <h5 style={styles.errorTitle}>Error Loading Data</h5>
                <p style={styles.errorMessage}>{error}</p>
                <button onClick={getContactedPeoplesList} style={styles.retryButton}>
                    Try Again
                </button>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            {/* Header with Tabs */}
            <div style={styles.header}>
                <div>
                    <div style={styles.tabContainer}>
                        {/* <button
                            onClick={() => handleTabChange('delhi-event')}
                            style={{
                                ...styles.tab,
                                ...(activeTab === 'delhi-event' ? styles.activeTab : {})
                            }}
                        >
                            Delhi Event Enquiry
                        </button> */}
                        <button
                            onClick={() => handleTabChange('website-contacts')}
                            style={{
                                ...styles.tab,
                                ...(activeTab === 'website-contacts' ? styles.activeTab : {})
                            }}
                        >
                            Expo Event Enquires
                        </button>
                    </div>
                    <p style={styles.subtitle}>
                        <span style={styles.statItem}>{data.length} total contacts</span>
                        <span style={styles.statDivider}>•</span>
                        <span style={styles.statItem}>{sortedData.length} filtered</span>
                        {selectedRows.size > 0 && (
                            <>
                                <span style={styles.statDivider}>•</span>
                                <span style={{ ...styles.statItem, color: '#f59e0b' }}>{selectedRows.size} selected</span>
                            </>
                        )}
                    </p>
                </div>
                <div style={styles.headerActions}>
                    <button onClick={exportSelectedData} style={styles.exportButton}>
                        Export {selectedRows.size > 0 ? `(${selectedRows.size})` : ''}
                    </button>
                    <button onClick={getContactedPeoplesList} style={styles.refreshButton}>
                        Refresh
                    </button>
                    <button onClick={handleLogout} style={styles.logoutButton}>
                        Logout
                    </button>
                </div>
            </div>

            {/* Stats Cards */}
            <div style={styles.statsGrid}>
                {[
                    { label: 'Total Contacts', value: data.length, color: '#3b82f6', icon: '👥' },
                    { label: 'Distribution', value: data.filter(i => i.interested_in === 'DISTRIBUTION').length, color: '#10b981', icon: '🏢' },
                    { label: 'Franchise', value: data.filter(i => i.interested_in === 'FRANCHISE').length, color: '#8b5cf6', icon: '👤' },
                    { label: 'Retail', value: data.filter(i => i.interested_in === 'MEDIA').length, color: '#f59e0b', icon: '📱' }
                ].map((stat, index) => (
                    <div key={index} style={styles.statCard}>
                        <div style={styles.statCardContent}>
                            <div style={{ ...styles.statIconContainer, backgroundColor: stat.color }}>
                                <span style={styles.statIcon}>{stat.icon}</span>
                            </div>
                            <div>
                                <div style={styles.statValue}>{stat.value}</div>
                                <div style={styles.statLabel}>{stat.label}</div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Filters */}
            <div style={styles.filtersSection}>
                <div style={styles.filtersRow}>
                    <div style={styles.searchBox}>
                        <input
                            type="text"
                            placeholder="Search by name, email, mobile, or message..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={styles.searchInput}
                        />
                        {searchTerm && (
                            <button onClick={() => setSearchTerm('')} style={styles.clearSearch}>
                                ×
                            </button>
                        )}
                    </div>

                    <select
                        value={filterInterest}
                        onChange={(e) => setFilterInterest(e.target.value)}
                        style={styles.filterSelect}
                    >
                        <option value="all">All Interests</option>
                        <option value="DISTRIBUTION">Distribution</option>
                        <option value="FRANCHISE">Franchise</option>
                        <option value="MEDIA">Media</option>
                    </select>

                    <select
                        value={itemsPerPage}
                        onChange={(e) => setItemsPerPage(Number(e.target.value))}
                        style={styles.perPageSelect}
                    >
                        <option value="10">10 per page</option>
                        <option value="25">25 per page</option>
                        <option value="50">50 per page</option>
                        <option value="100">100 per page</option>
                    </select>
                </div>

                {/* Active Filters */}
                {(searchTerm || filterInterest !== 'all') && (
                    <div style={styles.activeFilters}>
                        <span style={styles.activeFiltersLabel}>Active filters:</span>
                        {searchTerm && (
                            <span style={styles.filterBadge}>
                                "{searchTerm}"
                                <button onClick={() => setSearchTerm('')} style={styles.removeFilter}>×</button>
                            </span>
                        )}
                        {filterInterest !== 'all' && (
                            <span style={{ ...styles.filterBadge, ...getInterestBadge(filterInterest) }}>
                                Interest: {filterInterest}
                                <button onClick={() => setFilterInterest('all')} style={styles.removeFilter}>×</button>
                            </span>
                        )}
                        <button
                            onClick={() => {
                                setSearchTerm('');
                                setFilterInterest('all');
                            }}
                            style={styles.clearAllButton}
                        >
                            Clear All
                        </button>
                    </div>
                )}
            </div>

            {/* Bulk Actions */}
            {selectedRows.size > 0 && (
                <div style={styles.bulkActions}>
                    <span style={styles.bulkSelected}>{selectedRows.size} contacts selected</span>
                    <select
                        value={bulkAction}
                        onChange={(e) => setBulkAction(e.target.value)}
                        style={styles.bulkSelect}
                    >
                        <option value="">Bulk Actions</option>
                        <option value="delete">Delete Selected</option>
                        <option value="export">Export Selected</option>
                    </select>
                    <button
                        onClick={handleBulkAction}
                        disabled={!bulkAction}
                        style={styles.bulkApplyButton}
                    >
                        Apply
                    </button>
                </div>
            )}

            {/* Table */}
            <div style={styles.tableContainer}>
                <table style={styles.table}>
                    <thead style={styles.tableHead}>
                        <tr>
                            <th style={{ ...styles.tableHeader, width: '30px' }}>
                                <input
                                    type="checkbox"
                                    checked={selectedRows.size === currentItems.length && currentItems.length > 0}
                                    onChange={handleSelectAll}
                                    disabled={currentItems.length === 0}
                                    style={styles.checkbox}
                                />
                            </th>
                            <th style={styles.tableHeader} onClick={() => handleSort('name')}>
                                Name & Contact {getSortIndicator('name')}
                            </th>
                            <th style={styles.tableHeader} onClick={() => handleSort('brand')}>
                                Brand {getSortIndicator('brand')}
                            </th>
                            <th style={styles.tableHeader} onClick={() => handleSort('interested_in')}>
                                Interest {getSortIndicator('interested_in')}
                            </th>
                            <th style={styles.tableHeader}>City</th>
                            <th style={styles.tableHeader} onClick={() => handleSort('created_at')}>
                                Date {getSortIndicator('created_at')}
                            </th>
                            <th style={{ ...styles.tableHeader, textAlign: 'center' }}>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentItems.length > 0 ? (
                            currentItems.map((item) => (
                                <tr key={item.id} style={styles.tableRow}>
                                    <td style={styles.tableCell}>
                                        <input
                                            type="checkbox"
                                            checked={selectedRows.has(item.id)}
                                            onChange={() => handleSelectRow(item.id)}
                                            style={styles.checkbox}
                                        />
                                    </td>
                                    <td style={styles.tableCell}>
                                        <div style={styles.contactInfo}>
                                            <div style={styles.contactName}>{item.name}</div>
                                            <div style={styles.contactDetails}>
                                                <span style={styles.contactEmail}>{item.email}</span>
                                                <span style={styles.contactDot}>•</span>
                                                <span style={styles.contactPhone}>{item.mobile}</span>
                                            </div>
                                        </div>
                                    </td>
                                    <td style={styles.tableCell}>
                                        <span style={getInterestBadge(item.brand)}>
                                            {item.brand}
                                        </span>
                                    </td>
                                    <td style={styles.tableCell}>
                                        <span style={getInterestBadge(item.interested_in)}>
                                            {item.interested_in}
                                        </span>
                                    </td>
                                    <td style={styles.tableCell}>
                                        <div style={styles.messageCell}>
                                            <div style={styles.messageText}>
                                                {item?.city || "N/A"}
                                            </div>
                                            {/* {item.message.length > 150 && (
                                                <button
                                                    onClick={() => toggleMessageExpand(item.id)}
                                                    style={styles.expandButton}
                                                >
                                                    {expandedMessages.has(item.id) ? 'Show less' : 'Show more'}
                                                </button>
                                            )} */}
                                        </div>
                                    </td>
                                    <td style={styles.tableCell}>
                                        <span style={styles.dateText}>{formatDate(item.created_at)}</span>
                                    </td>
                                    <td style={{ ...styles.tableCell, textAlign: 'center' }}>
                                        <button
                                            onClick={() => handleOpenDeleteModal(item)}
                                            style={styles.deleteButton}
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="6" style={styles.emptyState}>
                                    <div style={styles.emptyStateContent}>
                                        <div style={styles.emptyIcon}>📭</div>
                                        <p style={styles.emptyText}>No contacts found</p>
                                        {(searchTerm || filterInterest !== 'all') && (
                                            <button
                                                onClick={() => {
                                                    setSearchTerm('');
                                                    setFilterInterest('all');
                                                }}
                                                style={styles.clearFiltersButton}
                                            >
                                                Clear Filters
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {sortedData.length > 0 && (
                <div style={styles.pagination}>
                    <div style={styles.paginationInfo}>
                        Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, sortedData.length)} of {sortedData.length}
                    </div>
                    <div style={styles.paginationControls}>
                        <button
                            onClick={() => setCurrentPage(1)}
                            disabled={currentPage === 1}
                            style={styles.paginationButton}
                        >
                            First
                        </button>
                        <button
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                            style={styles.paginationButton}
                        >
                            Previous
                        </button>
                        <span style={styles.pageIndicator}>
                            Page {currentPage} of {totalPages}
                        </span>
                        <button
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                            disabled={currentPage === totalPages}
                            style={styles.paginationButton}
                        >
                            Next
                        </button>
                        <button
                            onClick={() => setCurrentPage(totalPages)}
                            disabled={currentPage === totalPages}
                            style={styles.paginationButton}
                        >
                            Last
                        </button>
                    </div>
                </div>
            )}

            {/* Delete Modal */}
            {openDeleteModal && selectedContact && (
                <div style={styles.modalOverlay}>
                    <div style={styles.modal}>
                        <div style={styles.modalHeader}>
                            <h3 style={styles.modalTitle}>Confirm Delete</h3>
                            <button
                                onClick={() => {
                                    setOpenDeleteModal(false);
                                    setSelectedContact(null);
                                }}
                                style={styles.modalClose}
                            >
                                ×
                            </button>
                        </div>
                        <div style={styles.modalBody}>
                            <p style={styles.modalText}>
                                Are you sure you want to delete <strong>{selectedContact.name}</strong>'s contact?
                            </p>
                            <p style={styles.modalWarning}>This action cannot be undone.</p>
                        </div>
                        <div style={styles.modalFooter}>
                            <button
                                onClick={() => {
                                    setOpenDeleteModal(false);
                                    setSelectedContact(null);
                                }}
                                style={styles.modalCancelButton}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDelete}
                                disabled={deleteBtnLoading}
                                style={styles.modalDeleteButton}
                            >
                                {deleteBtnLoading ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Styles (same as KukoList but with adjustments for contact list)
const styles = {
    container: {
        padding: '24px',
        backgroundColor: '#f3f4f6',
        minHeight: '100vh',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '24px'
    },
    tabContainer: {
        display: 'flex',
        gap: '4px',
        marginBottom: '8px',
        borderBottom: '2px solid #e5e7eb'
    },
    tab: {
        padding: '10px 20px',
        fontSize: '15px',
        fontWeight: '500',
        color: '#6b7280',
        backgroundColor: 'transparent',
        border: 'none',
        borderBottom: '2px solid transparent',
        marginBottom: '-2px',
        cursor: 'pointer',
        transition: 'all 0.2s'
    },
    activeTab: {
        color: '#3b82f6',
        borderBottom: '2px solid #3b82f6',
        fontWeight: '600'
    },
    subtitle: {
        fontSize: '14px',
        color: '#6b7280',
        margin: 0
    },
    statItem: {
        marginRight: '8px'
    },
    statDivider: {
        marginRight: '8px',
        color: '#d1d5db'
    },
    headerActions: {
        display: 'flex',
        gap: '8px'
    },
    exportButton: {
        padding: '8px 16px',
        backgroundColor: '#10b981',
        color: 'white',
        border: 'none',
        borderRadius: '6px',
        fontSize: '14px',
        fontWeight: '500',
        cursor: 'pointer'
    },
    refreshButton: {
        padding: '8px 16px',
        backgroundColor: '#3b82f6',
        color: 'white',
        border: 'none',
        borderRadius: '6px',
        fontSize: '14px',
        fontWeight: '500',
        cursor: 'pointer'
    },
    logoutButton: {
        padding: '8px 16px',
        backgroundColor: '#ef4444',
        color: 'white',
        border: 'none',
        borderRadius: '6px',
        fontSize: '14px',
        fontWeight: '500',
        cursor: 'pointer'
    },
    statsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '16px',
        marginBottom: '24px'
    },
    statCard: {
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    },
    statCardContent: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
    },
    statDot: {
        width: '40px',
        height: '40px',
        borderRadius: '8px'
    },
    statValue: {
        fontSize: '20px',
        fontWeight: '600',
        color: '#111827',
        lineHeight: '1.2'
    },
    statLabel: {
        fontSize: '13px',
        color: '#6b7280'
    },
    filtersSection: {
        marginBottom: '16px'
    },
    filtersRow: {
        display: 'flex',
        gap: '12px',
        marginBottom: '12px'
    },
    searchBox: {
        flex: 2,
        position: 'relative'
    },
    searchInput: {
        width: '100%',
        padding: '10px 12px',
        border: '1px solid #e5e7eb',
        borderRadius: '6px',
        fontSize: '14px',
        outline: 'none',
        boxSizing: 'border-box'
    },
    clearSearch: {
        position: 'absolute',
        right: '10px',
        top: '50%',
        transform: 'translateY(-50%)',
        background: 'none',
        border: 'none',
        fontSize: '18px',
        color: '#9ca3af',
        cursor: 'pointer',
        padding: '0'
    },
    filterSelect: {
        flex: 1,
        padding: '10px 12px',
        border: '1px solid #e5e7eb',
        borderRadius: '6px',
        fontSize: '14px',
        outline: 'none',
        backgroundColor: 'white',
        cursor: 'pointer'
    },
    perPageSelect: {
        width: '120px',
        padding: '10px 12px',
        border: '1px solid #e5e7eb',
        borderRadius: '6px',
        fontSize: '14px',
        outline: 'none',
        backgroundColor: 'white',
        cursor: 'pointer'
    },
    activeFilters: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
        alignItems: 'center'
    },
    activeFiltersLabel: {
        fontSize: '13px',
        color: '#6b7280',
        marginRight: '4px'
    },
    filterBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '4px 8px',
        backgroundColor: '#e5e7eb',
        color: '#374151',
        borderRadius: '4px',
        fontSize: '12px',
        fontWeight: '500'
    },
    removeFilter: {
        background: 'none',
        border: 'none',
        color: 'inherit',
        fontSize: '14px',
        cursor: 'pointer',
        padding: '0 4px',
        opacity: 0.7
    },
    clearAllButton: {
        padding: '4px 12px',
        backgroundColor: 'transparent',
        border: '1px solid #d1d5db',
        borderRadius: '4px',
        fontSize: '12px',
        color: '#6b7280',
        cursor: 'pointer'
    },
    bulkActions: {
        backgroundColor: '#fef3c7',
        border: '1px solid #fde68a',
        borderRadius: '6px',
        padding: '12px 16px',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
    },
    bulkSelected: {
        fontSize: '14px',
        fontWeight: '500',
        color: '#92400e'
    },
    bulkSelect: {
        padding: '6px 12px',
        border: '1px solid #fde68a',
        borderRadius: '4px',
        fontSize: '13px',
        outline: 'none',
        backgroundColor: 'white',
        minWidth: '160px'
    },
    bulkApplyButton: {
        padding: '6px 16px',
        backgroundColor: '#f59e0b',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        fontSize: '13px',
        fontWeight: '500',
        cursor: 'pointer'
    },
    tableContainer: {
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        overflow: 'auto'
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: '14px'
    },
    tableHead: {
        backgroundColor: '#f9fafb',
        borderBottom: '1px solid #e5e7eb'
    },
    tableHeader: {
        padding: '12px 16px',
        textAlign: 'left',
        fontWeight: '500',
        color: '#374151',
        cursor: 'pointer',
        userSelect: 'none',
        whiteSpace: 'nowrap'
    },
    tableRow: {
        borderBottom: '1px solid #f3f4f6',
        cursor: 'pointer',
        transition: 'background-color 0.2s'
    },
    tableCell: {
        padding: '16px',
        color: '#111827'
    },
    checkbox: {
        width: '16px',
        height: '16px',
        cursor: 'pointer',
        margin: 0
    },
    contactInfo: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px'
    },
    contactName: {
        fontWeight: '500',
        color: '#111827'
    },
    contactDetails: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        fontSize: '12px',
        color: '#6b7280'
    },
    contactEmail: {
        color: '#3b82f6'
    },
    contactDot: {
        color: '#d1d5db'
    },
    contactPhone: {
        color: '#6b7280'
    },
    messageCell: {
        maxWidth: '400px'
    },
    messageText: {
        fontSize: '13px',
        color: '#4b5563',
        lineHeight: '1.5',
        marginBottom: '4px'
    },
    expandButton: {
        background: 'none',
        border: 'none',
        color: '#3b82f6',
        fontSize: '12px',
        cursor: 'pointer',
        padding: '4px 0',
        textDecoration: 'underline'
    },
    dateText: {
        fontSize: '13px',
        color: '#6b7280',
        whiteSpace: 'nowrap'
    },
    deleteButton: {
        padding: '4px 12px',
        backgroundColor: '#fee2e2',
        color: '#ef4444',
        border: '1px solid #fecaca',
        borderRadius: '4px',
        fontSize: '12px',
        fontWeight: '500',
        cursor: 'pointer'
    },
    emptyState: {
        padding: '48px',
        textAlign: 'center'
    },
    emptyStateContent: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '12px'
    },
    emptyIcon: {
        fontSize: '48px',
        color: '#9ca3af'
    },
    emptyText: {
        fontSize: '16px',
        color: '#6b7280',
        margin: 0
    },
    clearFiltersButton: {
        padding: '8px 16px',
        backgroundColor: '#f3f4f6',
        color: '#374151',
        border: '1px solid #e5e7eb',
        borderRadius: '6px',
        fontSize: '13px',
        cursor: 'pointer'
    },
    pagination: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: '20px'
    },
    paginationInfo: {
        fontSize: '14px',
        color: '#6b7280'
    },
    paginationControls: {
        display: 'flex',
        gap: '4px',
        alignItems: 'center'
    },
    paginationButton: {
        padding: '6px 12px',
        backgroundColor: 'white',
        border: '1px solid #e5e7eb',
        borderRadius: '4px',
        fontSize: '13px',
        color: '#374151',
        cursor: 'pointer'
    },
    pageIndicator: {
        padding: '6px 12px',
        fontSize: '13px',
        color: '#374151'
    },
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
    },
    modal: {
        backgroundColor: 'white',
        borderRadius: '8px',
        width: '400px',
        maxWidth: '90%',
        boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
    },
    modalHeader: {
        padding: '16px 20px',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    modalTitle: {
        fontSize: '16px',
        fontWeight: '600',
        color: '#111827',
        margin: 0
    },
    modalClose: {
        background: 'none',
        border: 'none',
        fontSize: '20px',
        color: '#9ca3af',
        cursor: 'pointer',
        padding: 0
    },
    modalBody: {
        padding: '20px'
    },
    modalText: {
        fontSize: '14px',
        color: '#374151',
        margin: '0 0 8px 0'
    },
    modalWarning: {
        fontSize: '13px',
        color: '#ef4444',
        margin: 0
    },
    modalFooter: {
        padding: '16px 20px',
        borderTop: '1px solid #e5e7eb',
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '8px'
    },
    modalCancelButton: {
        padding: '8px 16px',
        backgroundColor: 'white',
        border: '1px solid #e5e7eb',
        borderRadius: '6px',
        fontSize: '13px',
        color: '#374151',
        cursor: 'pointer'
    },
    modalDeleteButton: {
        padding: '8px 16px',
        backgroundColor: '#ef4444',
        color: 'white',
        border: 'none',
        borderRadius: '6px',
        fontSize: '13px',
        fontWeight: '500',
        cursor: 'pointer'
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: '#f3f4f6'
    },
    loadingSpinner: {
        width: '40px',
        height: '40px',
        border: '3px solid #e5e7eb',
        borderTopColor: '#3b82f6',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        marginBottom: '16px'
    },
    loadingText: {
        fontSize: '14px',
        color: '#6b7280',
        margin: 0
    },
    errorContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: '#f3f4f6',
        padding: '20px'
    },
    errorIcon: {
        fontSize: '48px',
        marginBottom: '16px'
    },
    errorTitle: {
        fontSize: '18px',
        fontWeight: '600',
        color: '#111827',
        margin: '0 0 8px 0'
    },
    errorMessage: {
        fontSize: '14px',
        color: '#6b7280',
        margin: '0 0 20px 0',
        textAlign: 'center'
    },
    retryButton: {
        padding: '10px 20px',
        backgroundColor: '#3b82f6',
        color: 'white',
        border: 'none',
        borderRadius: '6px',
        fontSize: '14px',
        fontWeight: '500',
        cursor: 'pointer'
    },
    statIconContainer: {
        width: '48px',
        height: '48px',
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#3b82f6', // This will be overridden by inline style
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
    },
    statIcon: {
        fontSize: '24px',
        color: 'white',
        lineHeight: 1
    },
};

// Add animation keyframes
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);

export default ContactPeoplesList;